import {
    getAllOrganizations,
    IOrganization,
    useOrganizationById,
    useOrganizationPages,
} from "@/api";
import { ORGANIZATION_QUERY_FILTER } from "@/config/constants";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { CheckIcon, ChevronDownIcon, XIcon } from "lucide-react";
import { useMemo } from "react";
import { Button } from "../ui/button";
import {
    Combobox,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxInput,
    ComboboxItem,
    ComboboxList,
    ComboboxSeparator,
    ComboboxTrigger,
    ComboboxValue,
} from "../ui/combobox";
import {
    ALL_ORGS_SENTINEL,
    createOrgChangeHandler,
} from "./organization-combobox.utils";
import {
    EntityCombobox,
    type EntityComboboxConfig,
    type EntityComboboxProps,
} from "./EntityCombobox";

export type {
    EntityComboboxProps,
    EntityComboboxValidity,
} from "./EntityCombobox";

const ORGANIZATIONS_PAGE_SIZE = 100;
const ENTITY_ORGANIZATIONS_PAGE_SIZE = 50;

export interface OrganizationEntityComboboxProps extends EntityComboboxProps {
    mode: "entity";
    onChangeName?: (name: string | null) => void;
}

export interface OrganizationFilterComboboxProps {
    mode?: "filter";
    value: number | null;
    onChange: (organizationId: number | null) => void;
    onChangeName?: (name: string | null) => void;
    required?: boolean;
    disabled?: boolean;
    error?: boolean;
    helperText?: string;
}

export type OrganizationComboboxProps =
    | OrganizationEntityComboboxProps
    | OrganizationFilterComboboxProps;

function FilterOrganizationCombobox({
    value,
    onChange,
    onChangeName,
    disabled = false,
}: OrganizationFilterComboboxProps) {
    const { data, isPending } = useQuery({
        queryKey: ["organizations-autocomplete", ORGANIZATION_QUERY_FILTER],
        queryFn: () =>
            getAllOrganizations(
                1,
                ORGANIZATIONS_PAGE_SIZE,
                ORGANIZATION_QUERY_FILTER
            ),
    });

    const organizationOptions = useMemo(() => data?.items ?? [], [data?.items]);

    const selectedOrganization = useMemo(
        () =>
            value === null
                ? ALL_ORGS_SENTINEL
                : (organizationOptions.find((org) => org.id === value) ??
                  ALL_ORGS_SENTINEL),
        [value, organizationOptions]
    );

    const handleValueChange = useMemo(
        () => createOrgChangeHandler(value, onChange, onChangeName),
        [value, onChange, onChangeName]
    );

    const handleClear = () => {
        onChange(null);
        onChangeName?.(null);
    };

    return (
        <Combobox
            items={organizationOptions}
            itemToStringLabel={(organization: IOrganization) =>
                organization.name
            }
            value={selectedOrganization}
            onValueChange={handleValueChange}
        >
            <ComboboxTrigger
                render={
                    <Button
                        variant="outline"
                        className="w-64 justify-between font-normal"
                        disabled={disabled}
                    >
                        <span className="min-w-0 flex-1 truncate text-left">
                            <ComboboxValue placeholder="All organizations" />
                        </span>
                        <span className="flex shrink-0 items-center gap-0.5">
                            {/* Stop mousedown so Base UI's trigger doesn't open the dropdown */}
                            <span
                                role="button"
                                aria-label="Clear organization"
                                className={cn(
                                    "text-muted-foreground rounded-sm p-0.5 transition-opacity duration-150",
                                    "hover:text-foreground",
                                    value !== null && !disabled
                                        ? "opacity-100"
                                        : "pointer-events-none opacity-0"
                                )}
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleClear();
                                }}
                            >
                                <XIcon className="size-3" />
                            </span>
                            <ChevronDownIcon className="text-muted-foreground pointer-events-none size-4" />
                        </span>
                    </Button>
                }
            />
            <ComboboxContent>
                <ComboboxInput
                    showTrigger={false}
                    placeholder="Search"
                    onKeyDown={(e) => {
                        if (
                            e.key === "Backspace" &&
                            (e.target as HTMLInputElement).value === ""
                        ) {
                            handleClear();
                        }
                    }}
                />
                <ComboboxEmpty>
                    {isPending ? "Loading..." : "No organizations found"}
                </ComboboxEmpty>
                {/* px-1 wrapper matches ComboboxList's p-1 so the rounded highlight
                    is inset from the popup walls the same way org items are. */}
                <div className="px-1 py-1">
                    <button
                        type="button"
                        className="hover:bg-accent hover:text-accent-foreground relative mt-1 flex w-full cursor-pointer items-center gap-2 rounded-md py-1 pr-8 pl-1.5 text-sm select-none"
                        onClick={() => handleValueChange(ALL_ORGS_SENTINEL)}
                    >
                        All organizations
                        {value === null && (
                            <span className="pointer-events-none absolute right-2 flex size-4 items-center justify-center">
                                <CheckIcon className="pointer-events-none size-4" />
                            </span>
                        )}
                    </button>
                </div>
                <ComboboxSeparator />
                <ComboboxList>
                    {(organization: IOrganization) => (
                        <ComboboxItem
                            key={organization.id}
                            value={organization}
                            className="cursor-pointer"
                        >
                            {organization.name}
                        </ComboboxItem>
                    )}
                </ComboboxList>
            </ComboboxContent>
        </Combobox>
    );
}

const organizationComboboxConfig: EntityComboboxConfig<IOrganization> = {
    pageSize: ENTITY_ORGANIZATIONS_PAGE_SIZE,
    copy: {
        searchPlaceholder: "Search organizations",
        loadingList: "Loading organizations…",
        listError: "Couldn't load organizations.",
        noResults: "No organizations found",
        loadingSelected: "Loading selected organization…",
        selectedNotFound: "The selected organization was not found.",
        selectedError: "Couldn't load the selected organization.",
        chooseRequired: "Choose an organization.",
        triggerLoading: "Loading organization…",
        triggerNotFound: "Organization not found",
        triggerUnavailable: "Organization unavailable",
        triggerEmpty: "Select an organization",
    },
    usePages: ({ pageCount, size, search, enabled }) =>
        useOrganizationPages({ pageCount, size, search, enabled }),
    useSelected: (id) => useOrganizationById(id),
};

function EntityOrganizationCombobox({
    mode: _mode,
    ...props
}: OrganizationEntityComboboxProps) {
    return <EntityCombobox config={organizationComboboxConfig} {...props} />;
}

export function OrganizationCombobox(props: OrganizationComboboxProps) {
    if (props.mode === "entity") {
        return <EntityOrganizationCombobox {...props} />;
    }
    return <FilterOrganizationCombobox {...props} />;
}
